/**
 * Color Contrast Utilities
 * 
 * Functions to calculate contrast ratios and determine optimal text colors
 * for accessibility (WCAG AA/AAA compliance)
 */

/**
 * Convert hex color to RGB
 * @param {string} hex - Hex color (with or without #)
 * @returns {object} RGB object with r, g, b values (0-255)
 */
function hexToRgb(hex) {
  if (!hex) return { r: 0, g: 0, b: 0 };
  
  // Remove # if present
  const cleanHex = hex.replace('#', '');
  
  // Handle 3-digit hex
  if (cleanHex.length === 3) {
    const r = parseInt(cleanHex[0] + cleanHex[0], 16);
    const g = parseInt(cleanHex[1] + cleanHex[1], 16);
    const b = parseInt(cleanHex[2] + cleanHex[2], 16);
    return { r, g, b };
  }
  
  // Handle 6-digit hex
  if (cleanHex.length === 6) {
    const r = parseInt(cleanHex.substr(0, 2), 16);
    const g = parseInt(cleanHex.substr(2, 2), 16);
    const b = parseInt(cleanHex.substr(4, 2), 16);
    return { r, g, b };
  }
  
  // Default to black if invalid
  return { r: 0, g: 0, b: 0 };
}

/**
 * Calculate relative luminance of a color (WCAG formula)
 * @param {object} rgb - RGB object with r, g, b values (0-255)
 * @returns {number} Luminance value (0-1)
 */
function getLuminance(rgb) {
  const { r, g, b } = rgb;
  
  // Normalize RGB values
  const normalize = (val) => {
    val = val / 255;
    return val <= 0.03928 ? val / 12.92 : Math.pow((val + 0.055) / 1.055, 2.4);
  };
  
  return 0.2126 * normalize(r) + 0.7152 * normalize(g) + 0.0722 * normalize(b);
}

/**
 * Calculate contrast ratio between two colors (WCAG formula)
 * @param {string} color1 - First color (hex)
 * @param {string} color2 - Second color (hex)
 * @returns {number} Contrast ratio (1-21)
 */
export function getContrastRatio(color1, color2) {
  const rgb1 = hexToRgb(color1);
  const rgb2 = hexToRgb(color2);
  
  const lum1 = getLuminance(rgb1);
  const lum2 = getLuminance(rgb2);
  
  const lighter = Math.max(lum1, lum2);
  const darker = Math.min(lum1, lum2);
  
  return (lighter + 0.05) / (darker + 0.05);
}

/**
 * Determine if a color is dark (for simple dark/light detection)
 * Uses relative luminance threshold
 * @param {string} color - Hex color
 * @returns {boolean} True if color is dark
 */
export function isDarkColor(color) {
  if (!color) return true; // Default to dark
  const rgb = hexToRgb(color);
  const luminance = getLuminance(rgb);
  return luminance < 0.5;
}

/**
 * Get the best text color (dark or light) for a given background color
 * Uses WCAG contrast ratios to ensure readability
 * @param {string} backgroundColor - Background color (hex)
 * @param {string} lightText - Light text color (default: white)
 * @param {string} darkText - Dark text color (default: near-black)
 * @returns {string} Best text color (lightText or darkText)
 */
export function getBestTextColor(backgroundColor, lightText = '#ffffff', darkText = '#1a1a1a') {
  if (!backgroundColor) return lightText; // Default to light text
  
  // Calculate contrast ratios
  const contrastWithLight = getContrastRatio(backgroundColor, lightText);
  const contrastWithDark = getContrastRatio(backgroundColor, darkText);
  
  // WCAG AA requires 4.5:1 for normal text, 3:1 for large text
  // We'll use 4.5:1 as our threshold for best readability
  const minContrast = 4.5;
  
  // If both meet minimum, choose the one with better contrast
  if (contrastWithLight >= minContrast && contrastWithDark >= minContrast) {
    return contrastWithLight > contrastWithDark ? lightText : darkText;
  }
  
  // If only one meets minimum, use that one
  if (contrastWithLight >= minContrast) return lightText;
  if (contrastWithDark >= minContrast) return darkText;
  
  // If neither meets minimum (shouldn't happen with good colors),
  // choose the one with better contrast
  return contrastWithLight > contrastWithDark ? lightText : darkText;
}

/**
 * Get text color style object for a given background color
 * @param {string} backgroundColor - Background color (hex)
 * @returns {object} Style object with color property
 */
export function getTextColorStyle(backgroundColor) {
  const textColor = getBestTextColor(backgroundColor);
  return { color: textColor };
}

/**
 * Get complete style object for background and text colors
 * @param {string} backgroundColor - Background color (hex)
 * @returns {object} Style object with backgroundColor and color properties
 */
export function getContrastStyles(backgroundColor) {
  return {
    backgroundColor: backgroundColor || '#2c3e50',
    color: getBestTextColor(backgroundColor)
  };
}
